import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  getLastVehicleSessionAsync,
  getDriverAsync,
  selectGetVehicleSessionStatus,
  selectGetIsDriverKnownStatus,
  selectLastSession,
  selectDriver,
} from "./driverKnownSlice";
import {
  selectQrCodeId,
  selectVehicleType,
  selectVehicleUuid,
} from "./vehicleLoginSlice";
import { ErrorPage } from "../components/ErrorPage";
import { LaunchPage } from "@qivia/ui";
import {
  driverLoginSlice,
  selectIsPhoneTokenDecoded,
} from "./driverLoginSlice";

export const DriverKnown = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const driver = useAppSelector(selectDriver);
  const isDriverKnown = driver !== null;
  const isDriverKnownStatus = useAppSelector(selectGetIsDriverKnownStatus);
  const checkVehicleSessionStatus = useAppSelector(
    selectGetVehicleSessionStatus,
  );
  const lastSession = useAppSelector(selectLastSession);
  const vehicleType = useAppSelector(selectVehicleType);
  const vehicleUuid = useAppSelector(selectVehicleUuid);
  const qrCodeId = useAppSelector(selectQrCodeId);
  const [urlSearchParams] = useSearchParams();
  const isPhoneTokenDecoded = useAppSelector(selectIsPhoneTokenDecoded);

  const isPersonalRestitutionFlow = useMemo(
    () =>
      isDriverKnown &&
      vehicleType === "PERSONAL" &&
      import.meta.env["VITE_TARGET_ENV"] === "development",
    [isDriverKnown, vehicleType],
  );

  const hasActiveSession = useMemo(
    () => lastSession?.driverUuid === driver,
    [lastSession, driver],
  );

  useEffect(() => {
    if (!qrCodeId) return;
    void dispatch(getDriverAsync({ uuid: qrCodeId }));
  }, [dispatch, navigate, qrCodeId]);

  useEffect(() => {
    if (isDriverKnownStatus !== "success") return;

    if (isPersonalRestitutionFlow) {
      if (vehicleUuid) {
        void dispatch(getLastVehicleSessionAsync({ vehicleUuid }));
      }
    } else if (isDriverKnown) {
      navigate("/menu");
    } else if (urlSearchParams.get("preserve_token") !== "true") {
      localStorage.removeItem("phoneToken");
      dispatch(driverLoginSlice.actions.decodePhoneToken());
    } else {
      navigate("/driver_register");
    }
  }, [
    isDriverKnownStatus,
    isPersonalRestitutionFlow,
    isDriverKnown,
    vehicleUuid,
    navigate,
    dispatch,
    urlSearchParams,
  ]);

  useEffect(() => {
    if (checkVehicleSessionStatus !== "success") return;
    if (hasActiveSession) {
      navigate("/menu");
    } else {
      navigate("/restitution_check_before_departure");
    }
  }, [checkVehicleSessionStatus, hasActiveSession, navigate]);

  useEffect(() => {
    const localStorageToken = localStorage.getItem("phoneToken");
    if (isDriverKnownStatus !== "success") return;
    if (isDriverKnown) return;
    if (urlSearchParams.get("preserve_token") === "true") return;
    if (!isPhoneTokenDecoded) return;
    if (localStorageToken) return;
    navigate("/driver_login");
  }, [
    isPhoneTokenDecoded,
    isDriverKnown,
    isDriverKnownStatus,
    navigate,
    dispatch,
    urlSearchParams,
  ]);

  if (
    isDriverKnownStatus === "processing" ||
    checkVehicleSessionStatus === "processing"
  ) {
    return <LaunchPage />;
  }

  if (isDriverKnownStatus === "failed" || qrCodeId === null)
    return <ErrorPage page="qrCode" />;
};
